/* istanbul ignore file */
import React from 'react';
import PropTypes from 'prop-types';
import { DefaultButton } from '@bingads-webui-react/fabric-control';
import { ImageCropper } from '@bingads-webui-campaign-react/image-cropper';
import { AddImageContainer } from '@bingads-webui-campaign-react/image-experience';

import { EditThumbnail } from './edit-thumbnail';
import C from '../../constants';
import { ImageExperienceVideo } from '../../model/image-experience-video';
import { CustomThumbnailRecordType } from '../../types';

export const useThumbnailEditor = (props) => {
  const {
    i18n,
    video,
    perfMarker,
    cid,
    aid,
    oDataConfig,
    isStockPhotoEnabled,
    isLazyLoadEnabled,
    permissions,
    staticImagePath,
    activityLogger,
    thumbnailRecord: initialThumbnailRecord,
  } = props;

  const [thumbnailRecord, setThumbnailRecord] = React.useState(initialThumbnailRecord);
  const initialMode = thumbnailRecord[C.videoTypeToThumbnailType[video.Type]] ? 'Manual' : 'Automatic';
  const [thumbnailMode, setThumbnailMode] = React.useState(initialMode);

  const component = (
    <ThumbnailEditor
      i18n={i18n}
      perfMarker={perfMarker}
      cid={cid}
      aid={aid}
      oDataConfig={oDataConfig}
      isStockPhotoEnabled={isStockPhotoEnabled}
      isLazyLoadEnabled={isLazyLoadEnabled}
      permissions={permissions}
      staticImagePath={staticImagePath}
      activityLogger={activityLogger}
      video={video}
      thumbnailRecord={thumbnailRecord}
      setThumbnailRecord={setThumbnailRecord}
      thumbnailMode={thumbnailMode}
      setThumbnailMode={setThumbnailMode}
    />);

  const getData = () => {
    if (thumbnailRecord === initialThumbnailRecord && thumbnailMode === initialMode) {
      return null;
    }
    activityLogger.signal(`update thumbnail: mode: ${thumbnailMode}`, 'video editor');
    const filteredThumbnailRecord = { ...thumbnailRecord };
    if (thumbnailMode === 'Automatic') {
      delete filteredThumbnailRecord[C.videoTypeToThumbnailType[video.Type]];
    }
    return filteredThumbnailRecord;
  };

  return { component, getData };
};

const ThumbnailEditor = (props) => {
  const {
    i18n,
    video,
    perfMarker,
    thumbnailRecord,
    setThumbnailRecord,
    thumbnailMode,
    setThumbnailMode,
    cid,
    aid,
    staticImagePath,
    oDataConfig,
    permissions,
    isStockPhotoEnabled,
  } = props;

  const [isImageSelectorDisplayed, setIsImageSelectorDisplayed] = React.useState(false);

  const onSelectedThumbnail = (image) => {
    image.setType(C.videoTypeToThumbnailType[video.Type]);
    setThumbnailRecord({
      ...thumbnailRecord,
      [image.Type]: image,
    });
    setIsImageSelectorDisplayed(false);
  };

  const onCropSettingsChanged = (cropSettings) => {
    const thumbnail = thumbnailRecord[C.videoTypeToThumbnailType[video.Type]];
    thumbnail.setCropSettings(cropSettings);
    setThumbnailRecord({
      ...thumbnailRecord,
      [thumbnail.Type]: thumbnail,
    });
  };

  const getThumbnailCropper = () => {
    const thumbnail = thumbnailRecord[C.videoTypeToThumbnailType[video.Type]];

    if (thumbnailMode === 'Manual' && !thumbnail) {
      return <div className="preview-unavailable">{i18n.getString('ImageExperience_VideoNoCustomThumbnail_NoPreview')}</div>;
    }

    return (
      <div className="thumbnail-cropper-container">
        <ImageCropper
          aspectRatio={C.videoAspectRatios[video.Type].ratio}
          boxHeight={360}
          minSize={C.videoAspectRatios[video.Type].thumbnailCropSetting.minSize}
          imageSource={thumbnail.Src}
          cropSettings={thumbnail.CropSettings}
          onCropSettingsChanged={onCropSettingsChanged}
        />
      </div>
    );
  };

  return (
    <React.Fragment>
      <div className="edit-container">
        <EditThumbnail
          i18n={i18n}
          onThumbnailModeChanged={setThumbnailMode}
          mode={thumbnailMode}
        />
      </div>
      { thumbnailMode === 'Manual' &&
        <div className="preview-container">
          {getThumbnailCropper()}
          <DefaultButton className="select-image-button" text={i18n.getString('ImageExperience_ChooseThumbnail')} onClick={() => setIsImageSelectorDisplayed(true)} />
        </div>
      }
      <AddImageContainer
        isImageSelectorDisplayed={isImageSelectorDisplayed}
        onSelectedImagesSet={onSelectedThumbnail}
        onImageSelectionCanceled={() => setIsImageSelectorDisplayed(false)}
        imageType={null}
        i18n={i18n}
        perfMarker={perfMarker.createChild('thumbnailPicker')}
        isChooseThumbnail
        isForVNext
        cid={cid}
        aid={aid}
        oDataConfig={oDataConfig}
        isLazyLoadEnabled
        staticImagePath={staticImagePath}
        permissions={permissions}
        isStockPhotoEnabled={isStockPhotoEnabled}
      />
    </React.Fragment>);
};

ThumbnailEditor.propTypes = {
  i18n: PropTypes.shape({
    getString: PropTypes.func.isRequired,
  }).isRequired,
  perfMarker: PropTypes.shape({
    willFetchFile: PropTypes.func.isRequired,
    willInit: PropTypes.func.isRequired,
    willFetchData: PropTypes.func.isRequired,
    willRender: PropTypes.func.isRequired,
    done: PropTypes.func.isRequired,
    createChild: PropTypes.func.isRequired,
  }).isRequired,
  oDataConfig: PropTypes.shape({
    odataRootUrl: PropTypes.string,
    odataPath: PropTypes.string,
    odataToken: PropTypes.string,
    applicationName: PropTypes.string,
    oDataTokenType: PropTypes.string,
  }),
  activityLogger: PropTypes.shape({
    signal: PropTypes.func.isRequired,
  }),
  permissions: PropTypes.shape({
    IsVideoAdEditorEnabled: PropTypes.bool,
  }),
  cid: PropTypes.string.isRequired,
  aid: PropTypes.string.isRequired,
  staticImagePath: PropTypes.string.isRequired,
  video: PropTypes.instanceOf(ImageExperienceVideo).isRequired,
  thumbnailRecord: CustomThumbnailRecordType,
  setThumbnailRecord: PropTypes.func.isRequired,
  thumbnailMode: PropTypes.string.isRequired,
  setThumbnailMode: PropTypes.func.isRequired,
  isStockPhotoEnabled: PropTypes.bool.isRequired,
};

ThumbnailEditor.defaultProps = {
  oDataConfig: {},
  activityLogger: null,
  permissions: {},
  thumbnailRecord: {},
};
